import { supabase, supabaseConfigError } from './supabase'

const LESSON_BUCKET = 'lesson-attachments'

export const validateVideoUrl = (url) => {
  if (!url) return true
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]{11}/i
  const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\/\d+/i
  return youtubeRegex.test(url) || vimeoRegex.test(url)
}

export const buildVideoEmbedUrl = (url) => {
  if (!url) return ''
  try {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/)
    if (youtubeMatch?.[1]) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
    if (vimeoMatch?.[1]) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`
    }
  } catch (error) {
    console.error('Error building embed URL', error)
  }
  return url
}

export const uploadLessonAttachment = async (file, courseId) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  const path = `${courseId || 'general'}/${Date.now()}-${file.name}`
  const { data, error } = await supabase.storage.from(LESSON_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false
  })
  if (error) {
    return { data: null, error }
  }
  const { data: publicUrlData } = supabase.storage.from(LESSON_BUCKET).getPublicUrl(data.path)
  return {
    data: {
      name: file.name,
      path: data.path,
      url: publicUrlData?.publicUrl || ''
    },
    error: null
  }
}

export const fetchLessonsForCourse = async (courseId) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  return supabase
    .from('lessons')
    .select('*')
    .eq('course_id', courseId)
    .order('day_number', { ascending: true })
    .order('order_index', { ascending: true })
}

export const updateLessonRecord = async (lessonId, payload) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  return supabase.from('lessons').update(payload).eq('id', lessonId).select().single()
}

export const deleteLessonRecord = async (lessonId) => {
  if (supabaseConfigError) {
    return { error: supabaseConfigError }
  }
  return supabase.from('lessons').delete().eq('id', lessonId)
}

export const createLessonRecord = async (payload) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  return supabase.from('lessons').insert([payload]).select().single()
}

export const createQuizForLesson = async (lessonId, quizConfig) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  return supabase.from('quizzes').insert([{ lesson_id: lessonId, config: quizConfig }]).select().single()
}

export const fetchLessonTemplates = async (subject) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  let query = supabase
    .from('lesson_templates')
    .select('*')
    .order('created_at', { ascending: false })

  if (subject) {
    query = query.ilike('subject', `${subject}%`)
  }

  return query
}

export const createLessonTemplate = async (payload) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }
  return supabase.from('lesson_templates').insert([payload]).select().single()
}

export const createLessonFromTemplate = async (templateId, overrides) => {
  if (supabaseConfigError) {
    return { data: null, error: supabaseConfigError }
  }

  const { data: template, error: templateError } = await supabase
    .from('lesson_templates')
    .select('*')
    .eq('id', templateId)
    .single()

  if (templateError) {
    return { data: null, error: templateError }
  }

  const payload = {
    title: template.title,
    description: template.description || '',
    subject: template.subject || '',
    day_number: Number(overrides?.day_number) || 1,
    order_index: Number(overrides?.order_index) || 0,
    content: template.content || '',
    video_url: buildVideoEmbedUrl(template.media_url || ''),
    attachments: template.attachments_metadata || [],
    course_id: overrides?.course_id,
    curriculum_id: overrides?.curriculum_id
  }

  const { data: lesson, error: lessonError } = await createLessonRecord(payload)
  if (lessonError) {
    return { data: null, error: lessonError }
  }

  if (template.quiz_schema?.length) {
    const { error: quizError } = await createQuizForLesson(lesson.id, template.quiz_schema)
    if (quizError) {
      return { data: null, error: quizError }
    }
  }

  return { data: lesson, error: null }
}
